import { styles } from './FlexText.css';
import React, { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { classNames } from '@jbhi-fi/lanyard-ui/global/utils';
import { getFillSize, getStyle, setStyles } from './utils';

export type FlexTextProps = {
  children: React.ReactNode;
  isWidthOnly?: boolean;
  className?: string;
  style?: React.CSSProperties;
  maxFontSizePx?: number;
  minFontSizePx?: number;
};

export const FlexText = ({
  children,
  isWidthOnly,
  className,
  maxFontSizePx = 200,
  minFontSizePx = 12,
  style = {},
}: FlexTextProps) => {
  const [size, setSize] = useState(minFontSizePx);
  const isMountedRef = React.useRef(true);
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const rulerRef = React.useRef<HTMLDivElement | null>(null);

  const createRuler = () => {
    if (!isMountedRef.current || !wrapperRef.current) return;
    const ruler = wrapperRef.current.cloneNode(true) as HTMLDivElement;
    ruler.id = `ruler-${uuidv4()}`;
    setStyles(ruler, {
      position: 'absolute',
      top: '0px',
      left: 'calc(100vw * 2)',
      width: getStyle(wrapperRef.current, 'width'),
      minHeight: `0`,
      maxHeight: getStyle(wrapperRef.current, 'max-height') || getStyle(wrapperRef.current, 'height'),
      height: getStyle(wrapperRef.current, 'height'),
    });
    wrapperRef.current.parentNode?.appendChild(ruler);

    rulerRef.current = ruler;
    return ruler;
  };

  const clearRuler = () => {
    if (rulerRef.current) {
      wrapperRef.current?.parentNode?.removeChild(rulerRef.current);

      rulerRef.current = null;
    }
  };

  const handleResize = () => {
    if (!isMountedRef.current || !wrapperRef.current) return;
    if (rulerRef.current) clearRuler();
    const ruler = createRuler();
    const fontSize = getFillSize(ruler, isWidthOnly, minFontSizePx, maxFontSizePx);

    setSize(fontSize);
    clearRuler();
  };

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      clearRuler();
    };
  }, []);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    handleResize();

    const resizeObserver = new ResizeObserver(entries => {
      if (entries.length) {
        handleResize();
      }
    });
    if (wrapper) {
      resizeObserver.observe(wrapper);
    }
    return () => {
      resizeObserver.disconnect();
    };
  }, [size, isWidthOnly, minFontSizePx, maxFontSizePx, children, className]);

  return (
    <div
      className={classNames(styles.flexText({ isWidthOnly: isWidthOnly }), className)}
      style={{ fontSize: `${size - 1}px`, ...style }}
      ref={wrapperRef}
    >
      {typeof children === 'string' ? <span>{children}</span> : children}
    </div>
  );
};
